'use client';

import { useRouter } from 'next/navigation';
import { useMemo } from 'react';

import { ROUTES, STORAGE_KEY } from '@/constants';
import { useHistoryStore } from '@/stores/HistoryStore/useHistoryStore';
import { closeResponsiveDialog } from '@/stores/ResponsiveDialogStore';

const NOT_ALLOW_BACK_ROUTE = [
  ROUTES.AUTH,
  ROUTES.EASY_CERTIFICATION,
  ROUTES.EASY_CERTIFICATION_CALLBACK,
  ROUTES.LOGIN,
  ROUTES.CROSS_DOMAIN_AUTH,
];

export const useCustomRouter = () => {
  const router = useRouter();

  const customRouter = useMemo(() => {
    const push = (
      href: string,
      options?: Exclude<Parameters<typeof router.push>[1], undefined>,
    ) => {
      return router.push(href, options);
    };

    const replace = (
      href: string,
      options?: Exclude<Parameters<typeof router.push>[1], undefined>,
    ) => {
      sessionStorage.setItem(STORAGE_KEY.REPLACE_MARK_KB, 'true');

      return router.replace(href, options);
    };

    const back = () => {
      // RN WebView container의 Android back도 window.router.back()을 호출한다.
      // 페이지 history fallback보다 ResponsiveDialog 닫기를 먼저 시도해야
      // 앱 back 버튼으로 열린 modal/drawer가 닫힌다.
      if (closeResponsiveDialog()) return;

      const { historyIndex, customHistory } = useHistoryStore.getState();

      const isEmptyHistory = historyIndex < 1;
      const prevHistoryPath = customHistory.get(historyIndex - 1);
      const isNotAllowBackRoute = prevHistoryPath
        ? NOT_ALLOW_BACK_ROUTE.some((route) =>
            prevHistoryPath.includes(route.pathname),
          )
        : true;

      if (isEmptyHistory || isNotAllowBackRoute)
        return replace(ROUTES.HOME.pathname);

      return router.back();
    };

    return {
      ...router,
      push,
      replace,
      back,
    };
  }, [router]);

  return customRouter;
};
